import { useCallback, useEffect, useState } from "react";
import { Archive, Download, Loader2, Search, X } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["new", "reviewed", "contacted", "qualified", "archived"];

const STATUS_STYLES = {
    new: "border-forest/40 text-forest",
    reviewed: "border-ink/30 text-ink/60",
    contacted: "border-ink/30 text-ink/60",
    qualified: "border-champagne/60 text-[#8a7320]",
    archived: "border-ink/15 text-ink/35",
};

const FIELD_LABELS = {
    company_name: "Company Name",
    country: "Country",
    industry: "Industry",
    website: "Company Website",
    contact_person: "Contact Person",
    job_title: "Job Title",
    email: "Email Address",
    phone: "Phone / WhatsApp",
    position: "Position / Job Title",
    workers: "Number of Workers Required",
    qualifications: "Required Qualifications",
    experience: "Years of Experience",
    start_date: "Expected Start Date",
    salary: "Salary / Compensation Range",
    accommodation: "Accommodation Provided",
    transportation: "Transportation Provided",
    talent_market: "Preferred Talent Market",
    additional: "Additional Requirements",
    name: "Name",
    interest: "Interested In",
    message: "Message",
};

const RECRUITMENT_FIELDS = [
    "company_name", "country", "industry", "website",
    "contact_person", "job_title", "email", "phone",
    "position", "workers", "qualifications", "experience", "start_date", "salary",
    "accommodation", "transportation", "talent_market", "additional",
];

const CONTACT_FIELDS = ["name", "email", "phone", "interest", "message"];

const formatDate = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

export default function AdminInbox({
    fixedType = "",
    activeKey = "recruitment",
    pageTitle = "Recruitment Requests",
    pageSubtitle = "Review employer recruitment requests, update their status and download attached requirement documents.",
}) {
    const { user, logout, hasPermission } = useAdminAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ type: fixedType, status: "", q: "", date_from: "", date_to: "" });
    const [selected, setSelected] = useState(null);
    const [error, setError] = useState("");

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            const res = await fetch(`${API}/api/admin/submissions?${params}`, { credentials: "include" });
            if (res.status === 401) {
                logout();
                return;
            }
            if (!res.ok) throw new Error();
            setItems(await res.json());
        } catch {
            setError("Could not load submissions. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [filters, logout]);

    useEffect(() => {
        if (!user) return;
        const t = setTimeout(fetchList, 300);
        return () => clearTimeout(t);
    }, [user, fetchList]);

    const openDetail = async (id) => {
        const res = await fetch(`${API}/api/admin/submissions/${id}`, { credentials: "include" });
        if (res.ok) setSelected(await res.json());
    };

    const updateStatus = async (id, status) => {
        const res = await fetch(`${API}/api/admin/submissions/${id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
        });
        if (res.ok) {
            setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
            setSelected((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
        }
    };

    const downloadAttachment = async (submission) => {
        const res = await fetch(`${API}/api/admin/submissions/${submission.id}/attachment`, {
            credentials: "include",
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = submission.attachment_original || "job-description";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!user) {
        return <AdminLoading />;
    }

    const requiredPermission = fixedType === "contact" ? "contact" : fixedType === "recruitment" ? "recruitment" : "";
    if (requiredPermission && !hasPermission(requiredPermission)) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active={activeKey} email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section={pageTitle.toLowerCase()} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active={activeKey} email={user.email} user={user} onLogout={logout} />

            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <div className="mb-8">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">Admin Portal</p>
                    <h1 className="mt-2 font-serif text-3xl font-medium tracking-tight">{pageTitle}</h1>
                    <p className="mt-1 text-sm text-ink/55">{pageSubtitle}</p>
                </div>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative flex-1">
                        <Search size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
                        <input
                            data-testid="inbox-search-input"
                            type="text"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            placeholder="Search company, name, email, position…"
                            className="w-full border border-ink/15 bg-ivory py-3 pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-forest"
                        />
                    </div>
                    {!fixedType && (
                        <select
                            data-testid="inbox-filter-type"
                            value={filters.type}
                            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                            className="cursor-pointer border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                        >
                            <option value="">All types</option>
                            <option value="recruitment">Recruitment</option>
                            <option value="contact">Contact enquiries</option>
                        </select>
                    )}
                    <select
                        data-testid="inbox-filter-status"
                        value={filters.status}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                        className="cursor-pointer border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                    >
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
                        ))}
                    </select>
                    <input
                        data-testid="inbox-filter-from"
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                        className="border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                        aria-label="From date"
                    />
                    <input
                        data-testid="inbox-filter-to"
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                        className="border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                        aria-label="To date"
                    />
                </div>

                {error && <p className="mt-6 text-sm text-ink/60">{error}</p>}

                <div className="mt-8" data-testid="inbox-list">
                    {loading && items.length === 0 ? (
                        <div className="flex justify-center py-20">
                            <Loader2 size={22} className="animate-spin text-forest" />
                        </div>
                    ) : items.length === 0 ? (
                        <p className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">
                            No submissions match these filters yet.
                        </p>
                    ) : (
                        items.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                data-testid={`submission-row-${item.id}`}
                                onClick={() => openDetail(item.id)}
                                className="group grid w-full grid-cols-[1fr_auto] items-center gap-x-6 gap-y-1 border-t border-ink/10 px-2 py-5 text-left transition-colors hover:bg-sand sm:grid-cols-[auto_1fr_auto_auto_auto]"
                            >
                                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/45">
                                    {item.type === "recruitment" ? "Recruitment" : "Contact"}
                                </span>
                                <span className="min-w-0">
                                    <span className="block truncate font-serif text-lg font-medium tracking-tight text-ink">
                                        {item.type === "recruitment" ? item.company_name : item.name}
                                    </span>
                                    <span className="block truncate text-xs text-ink/50">
                                        {item.type === "recruitment"
                                            ? `${item.position} · ${item.workers} workers · ${item.email}`
                                            : `${item.interest} · ${item.email}`}
                                    </span>
                                </span>
                                <span className="hidden text-xs text-ink/45 sm:block">{formatDate(item.created_at)}</span>
                                <span className={`hidden border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] sm:block ${STATUS_STYLES[item.status] || STATUS_STYLES.new}`}>
                                    {item.status}
                                </span>
                                <span className="text-forest opacity-0 transition-opacity group-hover:opacity-100">→</span>
                            </button>
                        ))
                    )}
                    <div className="border-t border-ink/10" />
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="submission-detail">
                    <div className="absolute inset-0 bg-night/50" onClick={() => setSelected(null)} />
                    <div className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
                                    {selected.type === "recruitment" ? "Recruitment Request" : "Contact Enquiry"} · {formatDate(selected.created_at)}
                                </p>
                                <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight">
                                    {selected.type === "recruitment" ? selected.company_name : selected.name}
                                </h2>
                            </div>
                            <button
                                type="button"
                                data-testid="submission-detail-close"
                                onClick={() => setSelected(null)}
                                aria-label="Close detail"
                                className="border border-ink/15 p-2 text-ink/60 transition-colors hover:border-forest hover:text-forest"
                            >
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="mt-8 border-t border-ink/10">
                            {(selected.type === "recruitment" ? RECRUITMENT_FIELDS : CONTACT_FIELDS).map((field) =>
                                selected[field] ? (
                                    <div key={field} className="grid grid-cols-[140px_1fr] gap-4 border-b border-ink/10 py-3.5 sm:grid-cols-[180px_1fr]">
                                        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
                                            {FIELD_LABELS[field]}
                                        </span>
                                        <span className="whitespace-pre-wrap break-words text-sm text-ink/80">
                                            {selected[field]}
                                        </span>
                                    </div>
                                ) : null
                            )}
                        </div>

                        {selected.attachment && (
                            <button
                                type="button"
                                data-testid="attachment-download-btn"
                                onClick={() => downloadAttachment(selected)}
                                className="mt-6 inline-flex items-center gap-2 border border-forest/30 px-5 py-3 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-ivory"
                            >
                                <Download size={16} strokeWidth={1.5} />
                                Download {selected.attachment_original || "job description"}
                            </button>
                        )}

                        <div className="mt-10">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">
                                Mark as
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {STATUSES.filter((s) => s !== selected.status).map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        data-testid={`status-${s}-btn`}
                                        onClick={() => updateStatus(selected.id, s)}
                                        className={`inline-flex items-center gap-1.5 border px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                                            s === "archived"
                                                ? "border-ink/15 text-ink/50 hover:border-ink/40"
                                                : "border-forest/30 text-forest hover:bg-forest hover:text-ivory"
                                        }`}
                                    >
                                        {s === "archived" && <Archive size={13} strokeWidth={1.5} />}
                                        {s[0].toUpperCase() + s.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
